import { ref, reactive, Ref } from 'vue'
import type { InterestTag } from '@/type/Appointment'

export interface useInterestTagsInterface {
  interestTags: Ref<InterestTag[]>
  selectedInterestTags: Record<number, boolean>
  isLoadingTags: Ref<boolean>
  onSelectInterestTag: (tagId: number) => void
  initializeInterestTags: (tags: InterestTag[]) => void
  getSelectedInterestTagNameArray: () => string[]
  getSelectedInterestTagIdsArray: () => number[]
}

/**
 * หัตถการที่สนใจของฟอร์มนัดหมาย - ไม่บังคับเลือก
 * ตัวเลือก = หัตถการใต้บริการที่เลือก (LMP-7840) ตั้งผ่าน useAppointmentDepositTypeWithInterestTag
 * ไม่มีตัวเลือก (ยังไม่เลือกบริการ / บริการไม่มีหัตถการ) = ซ่อนช่องหัตถการ
 * ล้างที่เลือกด้วย initializeInterestTags (ผ่านการเปลี่ยนบริการ) - ไม่มี reset แยก
 */
export function useInterestTags(): useInterestTagsInterface {
  const interestTags = ref<InterestTag[]>([])
  const selectedInterestTags = reactive<Record<number, boolean>>({})
  const isLoadingTags = ref(false)

  /**
   * Toggle interest tag selection
   */
  function onSelectInterestTag(tagId: number) {
    selectedInterestTags[tagId] = !selectedInterestTags[tagId]
  }

  /**
   * ตั้งตัวเลือกใหม่และล้างที่เลือกไว้ทั้งหมด - ลบ key ของตัวเลือกชุดเดิมด้วย ไม่ให้หัตถการของบริการเดิมค้างเป็น true
   * ใน state (selector อ่าน record นี้ตรงๆ)
   */
  function initializeInterestTags(tags: InterestTag[]) {
    interestTags.value = tags
    Object.keys(selectedInterestTags).forEach(key => {
      delete selectedInterestTags[Number(key)]
    })
    tags.forEach(tag => {
      selectedInterestTags[tag.id] = false
    })
  }

  /**
   * Get selected interest tags as an array of tag names
   */
  function getSelectedInterestTagNameArray(): string[] {
    return interestTags.value.filter(tag => selectedInterestTags[tag.id]).map(tag => tag.name)
  }

  /**
   * Get selected interest tag IDs as an array
   */
  function getSelectedInterestTagIdsArray(): number[] {
    return interestTags.value.filter(tag => selectedInterestTags[tag.id]).map(tag => tag.id)
  }

  return {
    interestTags,
    selectedInterestTags,
    isLoadingTags,
    onSelectInterestTag,
    initializeInterestTags,
    getSelectedInterestTagNameArray,
    getSelectedInterestTagIdsArray,
  }
}
